import React, { createContext, useContext } from 'react';
import { useCaseBuilderData } from '../hooks/useCaseBuilderData';
import { useCaseBuilder } from '../hooks/useCaseBuilder';
import { useBottleCarousel } from '../hooks/useBottleCarousel';
import { useSwapModal } from '../hooks/useSwapModal';

// Create Context
const CaseBuilderContext = createContext(null);

// Provider Component
export const CaseBuilderProvider = ({ children }) => {
	const queryParams = new URLSearchParams(window.location.search);
	const slugValue = queryParams.get('slug');

	// 1. Fetch data
	const {
		contentfulData,
		wines,
		loading: dataLoading,
		error: dataError,
	} = useCaseBuilderData(slugValue);

	// 2. Build case
	const { wineSlots, swapSlots, pricingSummary, isReady, handleSwap } =
		useCaseBuilder(wines, contentfulData);

	// 3. Carousel state
	const {
		activeBottleIndex,
		setActiveBottleIndex,
		carouselOffset,
		mobileBottleOffset,
		handleNext,
		handlePrev,
		handleBottleClick,
		handleCarouselDotClick,
		bottleSlideRefs,
		detailSlideRefs,
		touchHandlers,
	} = useBottleCarousel(wineSlots.length);

	// 4. Swap modal state
	const {
		showSwapModal,
		activeSwaps,
		activeSwapIndex,
		currentCarouselSlide,
		openModal,
		closeModal,
		nextSlide,
		previousSlide,
		goToSlide,
	} = useSwapModal();

	// Handle swap and update carousel position
	const swapWines = () => {
		const swapIndex = currentCarouselSlide;
		const slotIndex = handleSwap(swapIndex, activeSwapIndex);

		if (slotIndex !== undefined && slotIndex !== activeBottleIndex) {
			const bottleSlide = bottleSlideRefs.current[slotIndex];
			const detailSlide = detailSlideRefs.current[slotIndex];

			const bottleWidth = bottleSlide?.offsetWidth || 0;
			const detailWidth = detailSlide?.offsetWidth || 0;

			setActiveBottleIndex(slotIndex);
		}

		closeModal();
	};

	// Handle bottle swap - open modal
	const handleBottleSwap = (index) => {
		if (swapSlots[index]) {
			openModal(swapSlots[index], index);
		}
	};

	// Combine all state and actions into context value
	const value = {
		// Data
		contentfulData,
		wineSlots,
		swapSlots,
		pricingSummary,

		// Loading states
		dataLoading,
		dataError,
		isReady,

		// Carousel state
		activeBottleIndex,
		carouselOffset,
		mobileBottleOffset,
		bottleSlideRefs,
		detailSlideRefs,

		// Carousel actions
		handleNext,
		handlePrev,
		handleBottleClick,
		handleCarouselDotClick,
		handleBottleSwap,
		touchHandlers,

		// Modal state
		showSwapModal,
		activeSwaps,
		currentCarouselSlide,

		// Modal actions
		closeModal,
		nextSlide,
		previousSlide,
		goToSlide,
		swapWines,
	};

	return (
		<CaseBuilderContext.Provider value={value}>
			{children}
		</CaseBuilderContext.Provider>
	);
};

// Custom hook to use the context
export const useCaseBuilderContext = () => {
	const context = useContext(CaseBuilderContext);
	if (!context) {
		throw new Error(
			'useCaseBuilderContext must be used within CaseBuilderProvider'
		);
	}
	return context;
};
